import { DateRange } from '@branchjs/neo-components';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import startOfQuarter from 'date-fns/startOfQuarter';
import startOfWeek from 'date-fns/startOfWeek';
import subDays from 'date-fns/subDays';

type LightweightFiltersRangeVariant = typeof LIGHTWEIGHT_FILTERS_RANGES_KEYS[number];
const LIGHTWEIGHT_FILTERS_RANGES_KEYS = [
  'today',
  'yesterday',
  'last_7_days',
  'last_14_days',
  'last_30_days',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'this_quarter',
  'last_quarter',
  'custom',
] as const;

const LIGHTWEIGHT_FILTERS_DEFAULT_RANGES_LABELS_MAP: Record<LightweightFiltersRangeVariant, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last_7_days: 'Last 7 days',
  last_14_days: 'Last 14 days',
  last_30_days: 'Last 30 days',
  this_week: 'This week',
  last_week: 'Last week',
  this_month: 'This month',
  last_month: 'Last month',
  this_quarter: 'This quarter',
  last_quarter: 'Last quarter',
  custom: 'Custom',
};

export const mapRangeVariantToDateRange = (rangeVariant: LightweightFiltersRangeVariant): DateRange<Date> => {
  switch (rangeVariant) {
    case 'yesterday': {
      const yesterday = subDays(startOfDay(new Date()), 1);
      return [yesterday, yesterday];
    }
    case 'last_7_days': {
      const today = startOfDay(new Date());
      const sevenDaysAgo = subDays(today, 7);
      return [sevenDaysAgo, today];
    }
    case 'last_14_days': {
      const today = startOfDay(new Date());
      const fourteenDaysAgo = subDays(today, 14);
      return [fourteenDaysAgo, today];
    }
    case 'last_30_days': {
      const today = startOfDay(new Date());
      const thirtyDaysAgo = subDays(today, 30);
      return [thirtyDaysAgo, today];
    }
    case 'this_week': {
      const today = startOfDay(new Date());
      const firstDayOfWeek = startOfWeek(today);
      return [firstDayOfWeek, today];
    }
    case 'last_week': {
      const today = startOfDay(new Date());
      const firstDayOfWeek = startOfWeek(today);
      const lastDayOfPreviousWeek = subDays(firstDayOfWeek, 1);
      const firstDayOfPreviousWeek = startOfWeek(lastDayOfPreviousWeek);
      return [firstDayOfPreviousWeek, lastDayOfPreviousWeek];
    }
    case 'this_month': {
      const today = startOfDay(new Date());
      const firstDayOfMonth = startOfMonth(today);
      return [firstDayOfMonth, today];
    }
    case 'last_month': {
      const today = startOfDay(new Date());
      const firstDayOfMonth = startOfMonth(today);
      const lastDayOfPreviousMonth = subDays(firstDayOfMonth, 1);
      const firstDayOfPreviousMonth = startOfMonth(lastDayOfPreviousMonth);
      return [firstDayOfPreviousMonth, lastDayOfPreviousMonth];
    }
    case 'this_quarter': {
      const today = startOfDay(new Date());
      const firstDayOfQuarter = startOfQuarter(today);
      return [firstDayOfQuarter, today];
    }
    case 'last_quarter': {
      const today = startOfDay(new Date());
      const firstDayOfQuarter = startOfQuarter(today);
      const lastDayOfPreviousQuarter = subDays(firstDayOfQuarter, 1);
      const firstDayOfPreviousQuarter = startOfQuarter(lastDayOfPreviousQuarter);
      return [firstDayOfPreviousQuarter, lastDayOfPreviousQuarter];
    }
    case 'today':
    case 'custom':
    default: {
      const today = startOfDay(new Date());
      return [today, today];
    }
  }
};

export const getAllRanges = () => {
  return LIGHTWEIGHT_FILTERS_RANGES_KEYS.map((rangeVariant) => ({
    type: rangeVariant,
    label: LIGHTWEIGHT_FILTERS_DEFAULT_RANGES_LABELS_MAP[rangeVariant],
    getValue: () => mapRangeVariantToDateRange(rangeVariant),
  }));
};
